import { SystemPromptData } from "@/common/system-prompts";
import SystemPromptFilters from "@/components/system-prompts/SystemPromptFilters.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Toast } from "@/components/ui/toast.tsx";
import { getSystemPrompts, updateSystemPrompts } from "@/lib/serverComm.tsx";
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const SystemPrompts: React.FC = () => {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastTitle, setToastTitle] = useState("");
  const [toastDescription, setToastDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptData[] | null>(
    null,
  );
  const [filteredSystemPrompts, setFilteredSystemPrompts] = useState<
    SystemPromptData[] | null
  >(null);
  const [systemPromptToEdit, setSystemPromptToEdit] =
    useState<SystemPromptData | null>(null);
  const [activeSystemPromptPillars, setActiveSystemPromptPillars] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (isLoading || systemPrompts !== null) {
      return;
    }

    fetchData().then();
  }, [isLoading, systemPrompts]);

  useEffect(() => {
    // if nothing to filter --> exit
    if (systemPrompts === null) {
      return;
    }

    // if empty search --> reset to full-list
    if (activeSystemPromptPillars.length === 0) {
      setFilteredSystemPrompts(systemPrompts);
      return;
    }

    // otherwise we filter
    const filteredSystemPrompts = systemPrompts.filter((item) => {
      if (item.node === undefined) {
        return false; // some data is corrupted
      }

      const pillar = item.node.split(".")[0];

      if (pillar === undefined) {
        return false; // some data is corrupted
      }

      return activeSystemPromptPillars.includes(pillar);
    });

    setFilteredSystemPrompts(filteredSystemPrompts);
  }, [systemPrompts, activeSystemPromptPillars]);

  const onSaveChanges = async () => {
    if (systemPromptToEdit?.prompt) {
      await updateSystemPrompts(
        systemPromptToEdit.id,
        systemPromptToEdit.prompt,
      );
      await fetchData();
      closeEditModal();
      setIsToastOpen(true);
      setToastTitle("Changes were saved.");
      setToastDescription("Changes will take effect from the next update.");
    }
  };

  const openEditModal = (prompt: SystemPromptData) => {
    setSystemPromptToEdit({ ...prompt }); // clone object for editing
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSystemPromptToEdit(null);
  };

  const fetchData = async () => {
    setIsLoading(true);

    const response = await getSystemPrompts();

    setSystemPrompts(response);
    setIsLoading(false);
  };

  return (
    <ScrollArea className="h-full">
      <Toast
        open={isToastOpen}
        setOpen={setIsToastOpen}
        title={toastTitle}
        description={toastDescription}
      />
      <div className="p-8 w-full">
        <div className="flex items-center w-full gap-4">
          <Link to={`/`}>
            <ArrowLeft
              size={24}
              className="rounded-full text-sky-500 bg-white p-1"
            />
          </Link>
          <h1 className="text-sky-500 text-2xl font-bold">
            Prompt Administration
          </h1>
        </div>

        <p>This page is intended for Parita administration only.</p>

        <SystemPromptFilters
          systemPrompts={systemPrompts}
          onChange={setActiveSystemPromptPillars}
        ></SystemPromptFilters>

        {isLoading || filteredSystemPrompts === null ? (
          <p className="mt-4">Loading...</p>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {filteredSystemPrompts.map((data, index) => (
              <div key={index} className="flex-1">
                <div className="flex items-center w-full mb-3">
                  <div className="flex flex-col w-full gap-1">
                    <h1 className="text-l font-bold">{data.display_name}</h1>
                    <p>Node: {data.node}</p>
                  </div>

                  <div className="flex-1"></div>
                  <Button
                    className="bg-sky-500 rounded-full pl-12 pr-12 justify-central"
                    onClick={() => openEditModal(data)}
                  >
                    <span className="flex-1">Edit</span>
                  </Button>
                </div>

                <textarea
                  id="prompt"
                  value={data.prompt}
                  className="font-mono w-full h-[500px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isEditModalOpen} onOpenChange={closeEditModal}>
        <DialogContent className="w-[600px]">
          <DialogHeader>
            <DialogTitle>{systemPromptToEdit?.display_name}</DialogTitle>
            <DialogDescription>
              Node: {systemPromptToEdit?.node}
            </DialogDescription>
          </DialogHeader>

          <label
            htmlFor="prompt"
            className="block text-xs text-black font-medium mb-2"
          >
            Content
          </label>
          <textarea
            id="prompt"
            value={systemPromptToEdit?.prompt ?? ""}
            onChange={(e) =>
              setSystemPromptToEdit((prev) =>
                prev ? { ...prev, prompt: e.target.value } : prev,
              )
            }
            placeholder="Enter your prompt query here..."
            className="font-mono w-full h-[300px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <p className="mt-4 text-xs font-bold">
            Changes will take effect from the next update.
          </p>

          <div className="flex items-center w-full gap-2">
            <div className="flex-1"></div>
            <Button
              className="bg-sky-100 text-black rounded-full pl-12 pr-12 justify-central"
              onClick={closeEditModal}
            >
              <span className="flex-1">Cancel</span>
            </Button>
            <Button
              className="bg-sky-500 rounded-full pl-12 pr-12 justify-central"
              onClick={onSaveChanges}
            >
              <span className="flex-1">Save Changes</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ScrollArea>
  );
};

export default SystemPrompts;
